import styled from "@emotion/styled"
import { useQuery } from "@tanstack/react-query"
import { APIProvider, Map } from "@vis.gl/react-google-maps"
import {
  AttributeDto,
  InstallationStationDto,
  PointsOfInterestDto,
  PointsOfInterestDtoPaginatedList,
  TableDto,
  getPazomatInstallStationsAutocomplete,
  getPointsOfInterestAutocomplete,
  getWashStationsAutocomplete,
  getWashStationsSearchMetadata,
  postPazomatInstallStationsGetSearchMetaData,
  postPazomatInstallStationsSearch,
  postPointsOfInterestSearch,
  postPointsOfInterestSearchMetaData,
  postWashStationsSearch
} from "api"

import { css } from "@emotion/react"
import { createDownloadFuncDict } from "components/YellowMarketMap/YellowMapUtils"
import { generatePDF } from "lib/generatePDF"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import colors from "theme/colors"
import {
  Desktop,
  DownloadIcon,
  Mobile,
  PrintIcon,
  SearchInput,
  TitleWithLine,
  extractProperty,
  useIsDesktop,
} from "ui"
import PazomatAdvancedMarker from "./PazomatAdvancedMarker"
import PazomatListItem from "./PazomatListItem"
import PazomatTabs from "./PazomatTabs"
import { MapSection } from "./types"

const MOBILE_STATION_CARD_WIDTH = 280
const MOBILE_STATION_GAP = 12

const StyledContainer = styled.div`
  padding-top: 40px;
  padding-bottom: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 60px;
    padding-left: 146px;
    padding-right: 146px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-top: 80px;
    padding-left: 220px;
    padding-right: 220px;
  }
`

const StyledTitleWrapper = styled.div`
  ${(props) => props.theme.breakpoints.down("sm")} {
    padding-left: 28px;
    padding-right: 28px;
  }
`

const StyledMapWithSidebar = styled.div<{ $fullWidth: boolean }>`
  display: grid;
  grid-template-columns: 1fr;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: ${(p) =>
    p.$fullWidth ? "258.67fr 727.33fr" : "1fr"};
  }
`

const StyledMap = styled.div`
  position: relative;
`

const StyledSidebar = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    box-shadow: 0px 2.67px 8px 0px rgba(130, 136, 148, 0.5);
  }
`

const StyledSearchbar = styled.div`
  background: rgba(21, 62, 105, 0.08);
  border-bottom: 0.67px solid rgba(21, 62, 105, 0.3);
  padding: 16px 16px 18.67px 16px;
`

const StyledResultText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 13.33px;
  margin-bottom: 6px;
`

const StyledBigNumber = styled.span`
  font-size: 18.67px;
  font-weight: 500;
  line-height: 23.89px;
`

const StyledMobileWrapper = styled.div`
  width: 100vw;
  overflow: scroll;
  position: absolute;
  z-index: 2;
  bottom: 20px;
  left: 0px;
  overflow-x: scroll;
`

const StyledItemsList = styled.div<{ $withMap: boolean }>`
  display: flex;
  height: ${(p) => (p.$withMap ? "76px" : "300px")};
  overflow: ${(p) => (p.$withMap ? "auto" : "scroll")};
  gap: 12px;
  flex-direction: ${(p) => (p.$withMap ? "row" : "column")};
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: column;
    padding-top: 13.33px;
    height: 397.33px;
    overflow: scroll;
    padding-bottom: 23.33px;
    overflow-x: hidden;
  }
  ${(p) =>
    p.$withMap &&
    css`
      ${p.theme.breakpoints.down("sm")} {
        > div:first-child {
          margin-inline-start: 15px;
        }
      }
    `};
`

const StyledSearchInput = styled.div<{ $fullWidth: boolean }>`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(p) => (p.$fullWidth ? "auto" : "340px")};
  }
  input {
    font-size: 16px;
    &::placeholder {
      font-size: 16px;
    }
  }
`

const StyledActions = styled.div`
  border-top: 0.67px solid rgba(21, 62, 105, 0.3);
  padding: 5.33px 24px;
  background: rgba(246, 247, 249, 1);

  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: flex-start;
  }
`

const StyledButton = styled.button`
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 0px;
  color: rgba(0, 80, 201, 1);
  align-items: center;
  display: flex;
  cursor: pointer;
  gap: 4px;
`

const StyledMapContainer = styled.div`
  position: relative;
  height: 480px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 541px;
  }
`

const StyledAttributes = styled.div<{ $withMap: boolean }>`
  display: flex;
  overflow-x: scroll;
  gap: 12px;
  flex-wrap: nowrap;
  z-index: 1;
  position: ${(p) => (p.$withMap ? "absolute" : "unset")};
  white-space: nowrap;
  left: 0px;
  padding-right: 10px;
  padding-left: 30px;

  top: 16px;
  margin-top: ${(p) => (p.$withMap ? "0px" : "15px")};
  ${(p) =>
    !p.$withMap &&
    css`
      padding-top: 5px;
      padding-bottom: 5px;
    `}
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-wrap: wrap;
    width: auto;
  }
`

const StyledAttribute = styled.button<{ $selected: boolean }>`
  display: flex;

  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  padding: 6px 12px 6px 8px;
  background: ${(props) => (props.$selected ? " #5c6474" : "#FFF")};
  color: ${(props) => props.$selected && "#FFF"};
  border-radius: 20px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  img {
    filter: ${(props) => (props.$selected ? "brightness(0) invert(1)" : "")};
  }
`

const mapKey = process.env.GOOGLE_API_KEY as string

const sections: MapSection[] = [
  {
    name: "תחנות פזומט לתדלוק",
    type: "gaz",
    autocomplete: getPointsOfInterestAutocomplete,
    getMetadata: () =>
      postPointsOfInterestSearchMetaData("v1", { ForPointType: "Pazomat" }),
    getData: (term: string, selectedFilters: AttributeDto[]) =>
      postPointsOfInterestSearch("v1", {
        Type: "Pazomat",
        InstallerProvider: null,
        SearchTerm: term,
        PointTypes: [],
        Attributes: selectedFilters,
        Location: { Latitude: 0, Longitude: 0 },
        Distance: 5000000000 /* double */,
        RequestPage: 0 /* int32 */,
        PageSize: 500 /* int32 */,
        MetaData: []
      }),
    showMap: true,
    printIndex: {
      name: 1,
      address: 4,
      city: 6,
      attributes: 12,
    },
    hashValue: "fuel"
  },
  {
    name: "תחנות פזומט WASH",
    type: "wash",
    autocomplete: getWashStationsAutocomplete,
    getMetadata: () => getWashStationsSearchMetadata("v1"),
    getData: (term: string, selectedFilters: AttributeDto[]) =>
      postWashStationsSearch("v1", {
        SearchTerm: term,
        Attributes: selectedFilters,
      }),
    showMap: true,
    printIndex: {
      name: 2,
      address: 3,
      city: 6,
      attributes: 10,
    },
    hashValue: "wash"
  },
  {
    name: "תחנות התקנת פזומט אורפק",
    type: "install-orpak",
    autocomplete: getPazomatInstallStationsAutocomplete,
    getMetadata: () => postPazomatInstallStationsGetSearchMetaData("v1"),
    getData: (term: string, selectedFilters: AttributeDto[]) =>
      postPazomatInstallStationsSearch("v1", {
        SearchTerm: term,
        Attributes: selectedFilters,
        InstallationProvider: "Orpak",
      }),
    showMap: false,
    printIndex: {
      name: 1,
      address: 4,
      city: 6,
      attributes: 8,
    },
    hashValue: "orpak"
  },
  {
    name: "תחנות התקנת פזומט רוזמן",
    type: "install-rozman",
    autocomplete: getPazomatInstallStationsAutocomplete,
    getMetadata: () => postPazomatInstallStationsGetSearchMetaData("v1"),
    getData: (term: string, selectedFilters: AttributeDto[]) =>
      postPazomatInstallStationsSearch("v1", {
        SearchTerm: term,
        Attributes: selectedFilters,
        InstallationProvider: "Rozman",
      }),
    showMap: false,
    printIndex: {
      name: 1,
      address: 4,
      city: 6,
      attributes: 8,
    },
    hashValue: "rozman"
  }
]

function isPointsOfInterestDtoPaginatedList(
  obj: any,
): obj is PointsOfInterestDtoPaginatedList {
  if (obj) {
    return "Items" in obj
  }
  return false
}

function isPointsOfInterestDto(item: any): item is PointsOfInterestDto {
  return (item as PointsOfInterestDto).PrimaryTelephone !== undefined
}

function hasValidCoords(item: any): boolean {
  return (
    (item.Location &&
      item.Location.Latitude !== undefined &&
      item.Location.Longitude !== undefined) ||
    (item.Coordinate &&
      item.Coordinate.Latitude !== undefined &&
      item.Coordinate.Longitude !== undefined)
  )
}

function PazomatMap({ data }: { data: any }) {
  const isDesktop = useIsDesktop()
  const title = extractProperty(data, "title")
  const [selectedMarker, setSelectedMarker] = useState<string>("")
  const [value, setValue] = useState(0)
  const [indexStation, setSelectedIndex] = useState(0)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [selectedAttributes, setSelectedAttributes] = useState<AttributeDto[]>(
    [],
  )
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const newHash = "#" + sections[newValue].hashValue
    history.replaceState(null, '', newHash);
    setValue(newValue)
    setSelectedAttributes([])
  }

  const { data: queryData, isLoading } = useQuery({
    queryKey: ["GETDATA_PAZOMAT", value, searchTerm, selectedAttributes],
    queryFn: () => sections[value]?.getData(searchTerm, selectedAttributes),
    enabled: true,
    refetchOnWindowFocus: false,
  })

  const { data: attributesData, isLoading: isLoadingAttributes } = useQuery({
    queryKey: ["GETATTRIBUTES_PAZOMAT", value],
    queryFn: value === 0 ? undefined : sections[value]?.getMetadata,
    enabled: true,
    refetchOnWindowFocus: false,
  })

  const dataToMap = isPointsOfInterestDtoPaginatedList(queryData?.data?.Body)
    ? queryData?.data?.Body?.Items
    : queryData?.data?.Body || []

  const onPrint = () => {
    const array = dataToMap.map((object) => Object.values(object)) || []
    const printArray: TableDto = {
      Header: { Columns: ["שם התחנה", "כתובת", " מאפייני תחנה "] },
      Rows: array.map((el) => ({
        Columns: [
          el[sections[value]?.printIndex?.name || 0],
          `${el[sections[value]?.printIndex?.address || 0]}, ${el[sections[value]?.printIndex?.city || 0]}`,
          el[sections[value]?.printIndex?.attributes || 0]
            ?.map((item: any) => item?.Description)
            .join(" ,"),
        ],
      })),
      Footer: { Columns: [] },
      SubTitle: "",
      Title: "",
    }
    generatePDF([printArray])
  }
  const handleMapDownload = () => {
    const downloadFuncDict = createDownloadFuncDict(searchTerm)
    const sectionName = sections[value].type
    downloadFuncDict[sectionName]()
  }

  const containerRef = useRef<HTMLDivElement>(null)

  const calcScrollDistance = () => {
    if (!containerRef.current) {
      return
    }
    const index = indexStation
    const cardSize = (MOBILE_STATION_CARD_WIDTH + MOBILE_STATION_GAP)
    const currentX = index * cardSize

    containerRef.current.scrollTo({
      left: currentX * -1,
      behavior: 'smooth',
    },);
  }

  const getStationName = (item: InstallationStationDto | PointsOfInterestDto) => {
    if (sections[value].type === "gaz") {
      return (item as any).ShortName
    }
    return item.Name
  }

  useEffect(() => {
    if (!indexStation) {
      return
    }
    calcScrollDistance()
  }, [indexStation])

  useEffect(() => {
    const section = window?.location?.hash.slice(1);
    const currentTab = sections.findIndex((it) => it.hashValue === section)
    if (currentTab === -1) {
      return
    }
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollIntoView({ behavior: "smooth" })
    }
    setValue(currentTab)
  }, [])

  const getPhoneStationNumber = (item: InstallationStationDto | PointsOfInterestDto) => {
    if (sections[value].type === "gaz") {
      return undefined
    }
    return isPointsOfInterestDto(item)
      ? item?.PrimaryTelephone
      : item?.Telephone
  }
  const handleCloseMarkerWindow = (item: InstallationStationDto | PointsOfInterestDto) => {
    setSelectedMarker("")
    const name = getStationName(item)
    const stationCard = document.getElementById(name)
    if (stationCard) {
      stationCard.focus()
    }
  }

  return (
    <StyledContainer ref={scrollContainerRef}>
      <StyledTitleWrapper>
        <TitleWithLine titleColor={colors.black} title={title} />
      </StyledTitleWrapper>
      <PazomatTabs
        setValue={setValue}
        value={value}
        sections={sections}
        handleChange={handleChange}
      />
      <StyledMapWithSidebar $fullWidth={sections[value].showMap}>
        <StyledSidebar>
          <StyledSearchbar>
            <StyledResultText>
              נמצאו
              <StyledBigNumber> {dataToMap?.length} </StyledBigNumber>
              נקודות שירות
            </StyledResultText>
            <StyledSearchInput $fullWidth={sections[value].showMap}>
              <SearchInput
                height={!isDesktop ? "40px" : undefined}
                placeholder={"חפש שם תחנה / עיר /ישוב"}
                searchIcon={true}
                radius="200px"
                value={searchTerm}
                setValue={setSearchTerm}
                stopNavigation
                setSelectedMarker={setSelectedMarker}
              />
            </StyledSearchInput>
            {!isLoadingAttributes && !sections[value].showMap ? (
              <StyledAttributes $withMap={sections[value].showMap}>
                {attributesData?.data?.Body?.Attributes?.map((attr) => (
                  <StyledAttribute
                    onClick={() => {
                      setSelectedAttributes((prev) =>
                        prev.some((it) => it.Name === attr.Name)
                          ? prev.filter((it) => it.Name !== attr.Name)
                          : [...prev, attr],
                      )
                    }}
                    $selected={selectedAttributes.some(
                      (it) => it.Name === attr.Name,
                    )}
                    key={attr.Name}
                  >
                    <Image
                      src={attr.ImageUrl}
                      width={20}
                      style={{ objectFit: "contain" }}
                      height={20}
                      alt={''}
                    />
                    {attr.Description}
                  </StyledAttribute>
                ))}
              </StyledAttributes>
            ) : null}
          </StyledSearchbar>

          <Desktop>
            <StyledItemsList $withMap={sections[value].showMap}>
              {!isLoading &&
                dataToMap?.map((item, index) => {
                  return (
                    <PazomatListItem
                      title={getStationName(item)}
                      address={`${item?.Street}, ${item?.CityName}`}
                      phone={getPhoneStationNumber(item)}
                      selected={item.CrmId === selectedMarker}
                      key={index.toString()}
                      withMap={!!sections[value].showMap}
                      onClick={() => setSelectedMarker(item.CrmId)}
                    />
                  )
                }
                )}
            </StyledItemsList>
            <StyledActions>
              <StyledButton onClick={onPrint}>
                <PrintIcon width={24} height={24} />
                הדפסה
              </StyledButton>
              <StyledButton onClick={handleMapDownload}>
                <DownloadIcon />
                הורדת קובץ
              </StyledButton>
            </StyledActions>
          </Desktop>
        </StyledSidebar>
        {sections[value]?.showMap ? (
          <StyledMap>
            <APIProvider apiKey={mapKey} language="iw">
              <StyledMapContainer>
                <Mobile>
                  <StyledMobileWrapper >
                    <StyledItemsList $withMap={sections[value].showMap} ref={containerRef}>
                      {!isLoading &&
                        dataToMap?.map((item, index) => {
                          return (
                            <PazomatListItem
                              title={getStationName(item)}
                              address={`${item?.Street}, ${item?.CityName}`}
                              phone={getPhoneStationNumber(item)}
                              selected={item.CrmId === selectedMarker}
                              key={index.toString()}
                              withMap={!!sections[value].showMap}
                              width={MOBILE_STATION_CARD_WIDTH}
                            />
                          )
                        }
                        )}
                    </StyledItemsList>
                  </StyledMobileWrapper>
                </Mobile>
                {!isLoadingAttributes ? (
                  <StyledAttributes $withMap={sections[value].showMap}>
                    {attributesData?.data?.Body?.Attributes?.map((attr) => (
                      <StyledAttribute
                        onClick={() => {
                          setSelectedAttributes((prev) =>
                            prev.some((it) => it.Name === attr.Name)
                              ? prev.filter((it) => it.Name !== attr.Name)
                              : [...prev, attr],
                          )
                        }}
                        $selected={selectedAttributes.some(
                          (it) => it.Name === attr.Name,
                        )}
                        key={attr.Name}
                      >
                        <Image
                          src={attr.ImageUrl}
                          width={20}
                          style={{ objectFit: "contain" }}
                          height={20}
                          alt={attr.Description}
                        />
                        {attr.Description}
                      </StyledAttribute>
                    ))}
                  </StyledAttributes>
                ) : null}

                <Map
                  fullscreenControl={false}
                  mapId="811c65245afc97ba"
                  mapTypeControl={false}
                  defaultZoom={9}
                  defaultCenter={{
                    lat: 32,
                    lng: 34.8,
                  }}
                >
                  {!isLoading &&
                    dataToMap?.map((item, index) => {
                      if (hasValidCoords(item)) {
                        const { Latitude: Longitude, Longitude: Latitude } =
                          //@ts-ignore
                          item.Location || item.Coordinate
                        return (
                          <PazomatAdvancedMarker
                            title={getStationName(item)}
                            address={`${item?.Street}, ${item?.CityName}`}
                            phone={getPhoneStationNumber(item)}
                            onClick={() => {
                              setSelectedMarker(item.CrmId)
                              setSelectedIndex(index)
                            }}
                            onClose={() => handleCloseMarkerWindow(item)}
                            key={item.CrmId}
                            lng={Longitude}
                            lat={Latitude}
                            fill={"rgba(0, 80, 201, 1)"}
                            active={item.CrmId === selectedMarker}
                          />
                        )
                      } else {
                        // Generate random coordinates if neither Location nor Coordinate is available
                        return null
                      }
                    })}
                </Map>
              </StyledMapContainer>
            </APIProvider>
          </StyledMap>
        ) : null}
        <Mobile>
          {!sections[value]?.showMap ? (
            <StyledItemsList $withMap={sections[value].showMap}>
              {!isLoading &&
                dataToMap?.map((item, index) => (
                  <PazomatListItem
                    title={getStationName(item)}
                    address={`${item?.Street}, ${item?.CityName}`}
                    phone={getPhoneStationNumber(item)}
                    selected={item.CrmId === selectedMarker}
                    key={index.toString()}
                    withMap={!!sections[value]?.showMap}
                  />
                ))}
            </StyledItemsList>
          ) : null}
          <StyledActions>
            <StyledButton onClick={onPrint}>
              <PrintIcon />
              הדפסה
            </StyledButton>
            <StyledButton onClick={handleMapDownload}>
              <DownloadIcon />
              הורדת קובץ
            </StyledButton>
          </StyledActions>
        </Mobile>
      </StyledMapWithSidebar>
    </StyledContainer>
  )
}

export default PazomatMap
